function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs, form associations, and radio names so old hidden radios leave the active radio group
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form], input[type="radio"]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);

      if (element.tagName === 'INPUT' && element.type === 'radio' && element.name) {
        element.setAttribute('name', `${element.name}-${uniqueKey}`);
      }
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var newOpt = document.createElement('option');
        newOpt.value = provinces[i][0];
        newOpt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(newOpt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)',
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`,
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`),
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    const playButton = this.querySelector('.video__control.play');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
    if (!playButton) return;
    playButton.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));

      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO') {
        const videoElement = this.querySelector('video');

        let playControl = this.querySelector('.video__control.play');
        let pauseControl = this.querySelector('.video__control.pause');

        const triggerPause = () => {
          playControl.hidden = false;
          pauseControl.hidden = true;
          videoElement.pause();
        };

        const triggerPlay = () => {
          playControl.hidden = true;
          pauseControl.hidden = false;
          videoElement.play();
        };

        triggerPlay();
        deferredElement.addEventListener('click', () => {
          if (videoElement.paused) return triggerPlay();
          triggerPause();
        });

        // Cleanup deferred media loading
        playControl.removeEventListener('click', this.loadContent.bind(this));

        // Add play function to the play button
        playControl.addEventListener('click', () => {
          triggerPlay();
        });

        pauseControl.addEventListener('click', () => {
          triggerPause();
        });
      }
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        triggerPlay();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.enableSliderLooping = false;
    this.bindElements();

    this.initPages();
    if (!this.slider || !this.dotIndicators) return;

    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    if (!!this.prevButton && !!this.nextButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    this.dotIndicators[0]?.classList.toggle('pagination__item-active');
    this.dotIndicators.forEach((indicator) => {
      indicator.addEventListener('click', this.goToSlide?.bind(this));
    });

    this.sliderTrigger?.addEventListener('touchend', this.update?.bind(this));
  }

  bindElements() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dotIndicators = this.querySelectorAll('button.pagination__item');
    this.sliderTrigger = this.querySelector('button[data-pswp-src^="/cdn/"]');
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.dotIndicators) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    this.addEventListener('slideChanged', (evt) => {
      const currentElement = evt.detail.currentElement;
      if (!currentElement) return;
      const isVideoSlide = currentElement.querySelector('button[data-type="video"]');
      if (!isVideoSlide) window.pauseAllMedia();
      if (isVideoSlide) {
        const deferredMedia = currentElement.querySelector('deferred-media');
        if (deferredMedia && !deferredMedia.getAttribute('loaded')) {
          deferredMedia.loadContent();
        }
        const videoElement = currentElement.querySelector('video[id^="deferred-video-gid:"]');
        const playButton = currentElement.querySelector('.video__control.play');
        const pauseButton = currentElement.querySelector('.video__control.pause');

        if (videoElement && ((deferredMedia?.getAttribute('loaded') && videoElement?.paused) || videoElement.paused)) {
          videoElement.play();
          playButton.hidden = true;
          pauseButton.hidden = false;
        }
      }
    });

    if (this.dotIndicators) {
      this.dotIndicators.forEach((indicator) => {
        indicator.classList.remove('pagination__item-active');
      });
      this.dotIndicators[this.currentPage - 1]?.classList.toggle('pagination__item-active');
    }

    if (this.enableSliderLooping) return;

    if (!this.nextButton || !this.prevButton) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  goToSlide(event) {
    this.querySelector('.pagination__item-active')?.classList.remove('pagination__item-active');
    const index = event?.currentTarget?.dataset?.index;
    console.log({ index, sliderItemOffset: this.sliderItemOffset });
    if (isNaN(index)) return;

    const targetPosition = index * (this.sliderItemOffset ?? 0);
    console.log({ targetPosition });
    event.currentTarget.classList.toggle('pagination__item-active');
    this.setSlidePosition(targetPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  static SIZE_FIELDSET_SELECTOR = '[data-size-option-fieldset]';

  getSizeFieldset() {
    return this.querySelector(VariantSelects.SIZE_FIELDSET_SELECTOR);
  }

  constructor() {
    super();
    this._boundOnChange = this._boundOnChange || this.onChange.bind(this);
    this._boundOnSizePointerDown = this._boundOnSizePointerDown || this._onSizePointerDown.bind(this);
    this._boundOnSizeClick = this._boundOnSizeClick || this._onSizeClick.bind(this);
  }

  connectedCallback() {
    this.removeEventListener('change', this._boundOnChange);
    this.addEventListener('change', this._boundOnChange);
    this.removeEventListener('pointerdown', this._boundOnSizePointerDown, true);
    this.addEventListener('pointerdown', this._boundOnSizePointerDown, true);
    this.removeEventListener('click', this._boundOnSizeClick, true);
    this.addEventListener('click', this._boundOnSizeClick, true);
    // Restore saved selections FIRST so the width radio is checked before we
    // compute which sizes should be available. Otherwise updateAvailableOptions
    // runs with no checked width and treats all sizes as available.
    this._restorePendingSelections();
    this.updateAvailableOptions();
    const sizeFieldset = this.querySelector('[data-size-option-fieldset]');
    if (sizeFieldset) {
      this._syncInvalidSizeSelection(sizeFieldset, Array.from(sizeFieldset.querySelectorAll('input[type="radio"]')));
    }
    this.reconcileSizeSelectionUI();
    this._applyMatchingVariantToForm('connectedCallback');
  }

  _applyMatchingVariantToForm(source = 'unknown') {
    const matchingVariant = this.getMatchingVariant();
    const productInfo = this.closest('product-info');

    if (matchingVariant?.id && productInfo) {
      if (typeof productInfo.updateVariantInputs === 'function') {
        productInfo.updateVariantInputs(matchingVariant.id);
      } else if (!String(source).endsWith(':retry')) {
        queueMicrotask(() => this._applyMatchingVariantToForm(`${source}:retry`));
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._boundOnChange);
    this.removeEventListener('pointerdown', this._boundOnSizePointerDown, true);
    this.removeEventListener('click', this._boundOnSizeClick, true);
  }

  _onSizePointerDown(event) {
    const input = event.target.closest(` input[type="radio"]`);
    this._pointerDownSizeWasChecked = Boolean(input?.checked);
    this._pointerDownSizeInput = input ?? null;
  }

  /**
   * Re-clicking an already :checked size (common after browser back / bfcache) does not fire `change`.
   */
  _onSizeClick(event) {
    const input = event.target.closest(` input[type="radio"]`);
    if (!input || input !== this._pointerDownSizeInput) return;
    if (!this._pointerDownSizeWasChecked || !input.checked) return;

    this._resyncSizeSelection(input, 'sizeReselect');
  }

  _resyncSizeSelection(input, source) {
    this.updateSelectionMetadata({ target: input });
    this._applyMatchingVariantToForm(source);
    this.reconcileSizeSelectionUI();

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event: { target: input },
        target: this.getInputForEventTarget(input),
        selectedOptionValues: this.selectedOptionValues,
        force: true,
      },
    });
  }

  onChange(event) {
    const target = this.getInputForEventTarget(event.target);
    this.updateSelectionMetadata(event);

    this._applyMatchingVariantToForm('onChange');
    this.reconcileSizeSelectionUI();

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event,
        target,
        selectedOptionValues: this.selectedOptionValues,
      },
    });
  }

  _normalizeOptionValue(value) {
    return String(value ?? '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  _getVariantOptions(variant) {
    if (!variant) return [];

    if (Array.isArray(variant.options) && variant.options.length) {
      return variant.options.map((option) => this._normalizeOptionValue(option));
    }

    return [variant.option1, variant.option2, variant.option3]
      .filter((value) => value != null && value !== '')
      .map((option) => this._normalizeOptionValue(option));
  }

  getSelectedByPosition() {
    const selectedByPosition = {};

    // Use all checked radios (including disabled) so variant id matches visible selection.
    this.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
      const position = Number(input.dataset.optionPosition);
      if (!Number.isNaN(position)) {
        selectedByPosition[position] = this._normalizeOptionValue(input.value);
      }
    });

    return selectedByPosition;
  }

  _findVariantsByPositions(productJson, selectedByPosition) {
    const positions = Object.keys(selectedByPosition);
    if (!positions.length) return [];

    return productJson.filter((variant) => {
      const options = this._getVariantOptions(variant);
      if (!options.length) return false;

      return positions.every((position) => {
        const optionIndex = Number(position);
        return options[optionIndex] === selectedByPosition[position];
      });
    });
  }

  _findVariantsByCheckedSizeValue(productJson) {
    const sizeInput = this.querySelector('[data-size-option-fieldset] input[type="radio"]:checked');
    if (!sizeInput?.value) return [];

    const sizeValue = this._normalizeOptionValue(sizeInput.value);
    const otherSelections = this.getSelectedByPosition();
    const sizePosition = Number(sizeInput.dataset.optionPosition);

    return productJson.filter((variant) => {
      const options = this._getVariantOptions(variant);
      if (!options.length) return false;

      const hasSize = !Number.isNaN(sizePosition) ? options[sizePosition] === sizeValue : false;
      if (!hasSize) return false;

      return Object.entries(otherSelections).every(([position, value]) => {
        const optionIndex = Number(position);
        if (optionIndex === sizePosition) return true;
        return options[optionIndex] === value;
      });
    });
  }

  getMatchingVariant() {
    const productJson = this.getProductJson();
    if (!productJson.length) return null;

    const selectedByPosition = this.getSelectedByPosition();
    let matches = this._findVariantsByPositions(productJson, selectedByPosition);

    if (!matches.length) {
      matches = this._findVariantsByCheckedSizeValue(productJson);
    }

    if (!matches.length) return null;

    if (matches.length === 1) return matches[0];

    const checkedSize = this._normalizeOptionValue(
      this.getSizeFieldset()?.querySelector('input[type="radio"]:checked')?.value ?? '',
    );

    const exactTitleMatch = matches.find((variant) => this._normalizeOptionValue(variant.title) === checkedSize);
    if (exactTitleMatch) return exactTitleMatch;

    return matches.find((variant) => variant.available) ?? matches[0];
  }

  getProductJson() {
    const localRoot = this.closest('[data-replaceable-variants], [data-variants-complete-with]')?.parentElement;
    const script =
      localRoot?.querySelector('[type="application/json"][data-variants]') ||
      document.querySelector('[type="application/json"][data-variants]');

    if (!script?.textContent) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('VariantSelects: Failed to parse product variants JSON', error);
      return [];
    }
  }

  updateAvailableOptions() {
    const productJson = this.getProductJson();
    if (!productJson.length) return;

    const sizeFieldset = this.querySelector('[data-size-option-fieldset]');
    if (!sizeFieldset) return;

    const sizeInputs = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
    if (!sizeInputs.length) return;

    const selectedByPosition = {};
    this.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
      const position = Number(input.dataset.optionPosition);
      if (!Number.isNaN(position)) {
        selectedByPosition[position] = this._normalizeOptionValue(input.value);
      }
    });

    sizeInputs.forEach((input) => {
      const sizePosition = Number(input.dataset.optionPosition);
      const inputValue = this._normalizeOptionValue(input.value);
      const isAvailable = productJson.some((variant) => {
        if (!variant?.available) return false;

        const options = this._getVariantOptions(variant);
        if (!options.length) return false;

        if (!Number.isNaN(sizePosition) && options[sizePosition] !== inputValue) {
          return false;
        }

        return Object.entries(selectedByPosition).every(([position, selectedValue]) => {
          const optionIndex = Number(position);
          if (optionIndex === sizePosition || Number.isNaN(optionIndex)) return true;
          return options[optionIndex] === selectedValue;
        });
      });

      input.classList.toggle('is-unavailable', !isAvailable);
      if (isAvailable) {
        input.removeAttribute('aria-disabled');
      } else {
        input.setAttribute('aria-disabled', 'true');
      }
      // Keep unavailable sizes interactive (notify-me); only `disabled` attr should hard-disable.
      input.disabled = false;
    });

    this._syncInvalidSizeSelection(sizeFieldset, sizeInputs);
  }

  _clearSizeVisualSelection(sizeFieldset) {
    sizeFieldset.querySelectorAll('.grid-selector__label--inner-container-selected').forEach((element) => {
      element.classList.remove('grid-selector__label--inner-container-selected');
    });
  }

  _updateOptionLabel(fieldset, value) {
    const $label = fieldset?.querySelector('.product-selector-label-current-selection');
    if (!$label) return;

    const $sizeValue = $label.querySelector('[data-size-value]');
    if ($sizeValue) {
      $sizeValue.textContent = value;
      return;
    }

    const sizeText = ` ${value}`;
    if (typeof window.utilities?.replaceTextOnly === 'function') {
      window.utilities.replaceTextOnly($label, sizeText);
      return;
    }

    Array.from($label.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .forEach((node) => node.remove());

    const genderLabel = $label.querySelector('.product-selector-label-current-selection--gender-label');
    if (genderLabel) {
      genderLabel.insertAdjacentText('afterend', sizeText);
      return;
    }

    $label.appendChild(document.createTextNode(sizeText));
  }

  _updateSizeSelectionDisplay(sizeFieldset, selectedInput) {
    if (!sizeFieldset || !selectedInput) return;

    this._clearSizeVisualSelection(sizeFieldset);

    if (this._isSelectableInput(selectedInput)) {
      selectedInput
        .closest('.grid-selector__wrapper')
        ?.querySelector('.grid-selector__label--inner-container')
        ?.classList.add('grid-selector__label--inner-container-selected');
    }

    this._updateOptionLabel(sizeFieldset, selectedInput.value);
  }

  /**
   * Single source of truth for size label + highlight rings.
   * Prevents double borders (stale .grid-selector__label--inner-container-selected on one size,
   * :checked styles on another) after section swaps or programmatic checked changes.
   */
  reconcileSizeSelectionUI() {
    const sizeFieldset = this.querySelector('[data-size-option-fieldset]');
    if (!sizeFieldset) return;

    const checked = sizeFieldset.querySelector('input[type="radio"]:checked');

    this._clearSizeVisualSelection(sizeFieldset);

    if (!checked) return;

    this._updateOptionLabel(sizeFieldset, checked.value);

    if (this._isSelectableInput(checked)) {
      checked
        .closest('.grid-selector__wrapper')
        ?.querySelector('.grid-selector__label--inner-container')
        ?.classList.add('grid-selector__label--inner-container-selected');
    }
  }

  syncSizeLabelFromSelection() {
    this.reconcileSizeSelectionUI();
  }

  _isSelectableInput(input) {
    return !input.disabled && !input.classList.contains('is-unavailable');
  }

  /**
   * When width (or another option) changes, the checked size may become disabled while staying
   * checked — later clicks on that size do not fire `change`. Clear or replace invalid checks.
   */
  _syncInvalidSizeSelection(sizeFieldset, sizeInputs) {
    if (this._syncingSize) return;

    const checkedSize = sizeInputs.find((input) => input.checked);
    if (!checkedSize || this._isSelectableInput(checkedSize)) return;

    this._syncingSize = true;

    try {
      checkedSize.checked = false;
      checkedSize.setAttribute('aria-checked', 'false');
      this._clearSizeVisualSelection(sizeFieldset);

      const position = checkedSize.dataset.optionPosition;
      if (position !== undefined && VariantSelects._pendingSelections) {
        delete VariantSelects._pendingSelections[position];
      }

      const replacement = sizeInputs.find((input) => this._isSelectableInput(input));
      if (!replacement) return;

      replacement.checked = true;
      replacement.setAttribute('aria-checked', 'true');

      if (position !== undefined) {
        VariantSelects._pendingSelections = VariantSelects._pendingSelections || {};
        VariantSelects._pendingSelections[position] = replacement.value;
      }

      this._updateSizeSelectionDisplay(sizeFieldset, replacement);

      replacement.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      this._syncingSize = false;
    }
  }

  clearInvalidSizeSelection() {
    const sizeFieldset = this.querySelector('[data-size-option-fieldset]');
    if (!sizeFieldset) return;

    const sizeInputs = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
    sizeInputs.forEach((input) => {
      input.checked = false;
      input.setAttribute('aria-checked', 'false');
    });

    this._clearSizeVisualSelection(sizeFieldset);

    const position = sizeInputs[0]?.dataset?.optionPosition;
    if (position !== undefined && VariantSelects._pendingSelections) {
      delete VariantSelects._pendingSelections[position];
    }
  }

  updateSelectionMetadata({ target }) {
    if (!this.contains(target)) return;
    const { value, tagName } = target;

    if (target.type === 'radio') {
      target.checked = true;
      target.setAttribute('aria-checked', 'true');
    }

    this.updateAvailableOptions();

    // Persist selection so connectedCallback can restore it after viewTransition replaces the DOM
    if (target.type === 'radio' && target.dataset.optionPosition !== undefined) {
      VariantSelects._pendingSelections = VariantSelects._pendingSelections || {};
      VariantSelects._pendingSelections[target.dataset.optionPosition] = value;
    }

    const sizeFieldset = target.closest(VariantSelects.SIZE_FIELDSET_SELECTOR);
    if (sizeFieldset && tagName === 'INPUT' && target.type === 'radio') {
      this._updateSizeSelectionDisplay(sizeFieldset, target);
    } else {
      const optionFieldset = target.closest('fieldset.product-form__input');
      if (optionFieldset && tagName === 'INPUT' && target.type === 'radio') {
        this._updateOptionLabel(optionFieldset, value);
      }
    }

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const selectedOption = Array.from(target.options).find((option) => option.getAttribute('selected'));
      selectedOption?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset',
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  _restorePendingSelections() {
    const pending = VariantSelects._pendingSelections;
    if (!pending) return;

    Object.entries(pending).forEach(([position, value]) => {
      const inputs = Array.from(
        this.querySelectorAll(`fieldset input[type="radio"][data-option-position="${position}"]`),
      );
      const available = inputs.filter((i) => !i.classList.contains('is-unavailable') && !i.disabled);
      const match = available.find((i) => i.value === value) ?? available[0];
      if (match) {
        pending[position] = match.value;
        match.checked = true;
        match.setAttribute('aria-checked', 'true');

        const fieldset = match.closest('fieldset');
        if (fieldset?.id === 'product-form-size-option') {
          this._updateSizeSelectionDisplay(fieldset, match);
        } else if (fieldset) {
          this._updateOptionLabel(fieldset, match.value);
        }
      }
    });
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    // Use all checked radios (including briefly disabled during width swaps) so section
    // fetch option_values matches what the user sees — excluding them caused stale server HTML.
    return Array.from(this.querySelectorAll('select option[selected], fieldset input[type="radio"]:checked'))
      .map(({ dataset }) => dataset.optionValueId)
      .filter(Boolean);
  }

  static capturePendingSelectionsFromDom(root = document) {
    VariantSelects._pendingSelections = null;

    root.querySelectorAll('variant-selects').forEach((variantSelects) => {
      variantSelects.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
        if (input.dataset.optionPosition === undefined) return;

        VariantSelects._pendingSelections = VariantSelects._pendingSelections || {};
        VariantSelects._pendingSelections[input.dataset.optionPosition] = input.value;
      });
    });
  }

  static removeHiddenVariantSelectClones() {
    document.querySelectorAll('variant-selects').forEach((node) => {
      if (node.style.display === 'none') {
        node.remove();
      }
    });
  }

  static resyncAllAfterPageRestore(source = 'page-restore') {
    VariantSelects.removeHiddenVariantSelectClones();

    document
      .querySelectorAll('product-info[data-mvp-product-section], product-info[id^="MainProduct"]')
      .forEach((productInfo) => {
        if (typeof productInfo.resyncAfterPageRestore === 'function') {
          productInfo.resyncAfterPageRestore(source);
        }
      });
  }
}

customElements.define('variant-selects', VariantSelects);
VariantSelects._pendingSelections = null;

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));

    const params = new URLSearchParams(window.location.search);
    if (params.get('onboarding_success') === 'true') {
      this.setOnboardingToast();
    }
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }

  setOnboardingToast() {
    this.removeOnboardingQuery();

    this.classList.add('onboarding-toast-active');

    setTimeout(() => {
      this.classList.remove('onboarding-toast-active');
    }, 5000);
  }

  removeOnboardingQuery() {
    const url = new URL(window.location.href);
    url.searchParams.delete('onboarding_success');
    window.history.replaceState({}, '', url);
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

document.addEventListener('DOMContentLoaded', (event) => {
  detectPreferredMotion();
});

function detectPreferredMotion() {
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  //If the user prefers reduced motion
  if (mediaQuery?.matches) {
    //Grab all video buttons that aren't hidden.
    const videoButtons = document.querySelectorAll('.video__control:not([hidden])');

    //Return if videoButtons is undefined or empty
    if (!videoButtons || typeof videoButtons === 'undefined') return;

    videoButtons?.forEach((button) => {
      //Simulate click on all pause buttons to stop the video(s).
      //Ideally this would be done in Liquid before the render but that's not possible, so it must be done in JS this way.
      if (button?.classList?.contains('pause')) {
        button?.click();
      }
    });
  }
}
